import React, { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { SkeletonLine } from 'components/Skeleton'
import { fetchProgramme } from 'services/ProgrammeService'
import { updateProgrammeObligation } from 'services/complianceService'
import { getTask, updateTask } from 'services/taskService'
import { PRIVATE_PATHS } from 'utils/paths'
import { serverMessage } from './formGeneration'

const findObligation = (programme, obligationId) => {
  for (const domain of programme?.domains || []) {
    const hit = (domain.obligations || []).find(o => String(o.id) === String(obligationId))
    if (hit) return hit
  }
  return null
}

export const openTasksSentence = count => (count === 1
  ? 'Also move the 1 open task onto it (any answers already entered on it will be cleared)'
  : `Also move the ${count} open tasks onto it (any answers already entered on them will be cleared)`)

export const returnPathFor = ({ targetType, targetId, buildingPassportId }) => {
  if (targetType === 'task') return PRIVATE_PATHS.taskDetails.replace(':id', targetId)
  if (targetType === 'programme_obligation' && buildingPassportId) {
    return `${PRIVATE_PATHS.programme.replace(':bpId', buildingPassportId)}?obligation=${targetId}`
  }
  return null
}

const AttachDraftDialog = ({ targetType, targetId, buildingPassportId, templateId, onDone, onClose }) => {
  const titleId = useId()
  const [target, setTarget] = useState(null)
  const [loading, setLoading] = useState(true)
  const [applyToOpen, setApplyToOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const isObligation = targetType === 'programme_obligation'

  useEffect(() => {
    let live = true
    setLoading(true)
    const load = isObligation
      ? fetchProgramme(buildingPassportId).then(res => {
        const obligation = findObligation(res?.data?.data, targetId)
        return obligation
          ? { name: obligation.name, openTaskCount: Number(obligation.openTaskCount ?? (obligation.openTask ? 1 : 0)) || 0 }
          : null
      })
      : getTask(targetId).then(res => {
        const task = res?.data?.data
        return task ? { name: task.title, openTaskCount: 0 } : null
      })
    load
      .then(info => { if (live) setTarget(info) })
      .catch(() => { if (live) setTarget(null) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [isObligation, buildingPassportId, targetId])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const fallbackName = isObligation ? 'this obligation' : 'this task'
  const name = target?.name || fallbackName

  const applyForm = async () => {
    setSaving(true)
    try {
      if (isObligation) {
        const payload = { form_template_id: templateId }
        if (applyToOpen) payload.apply_form_to_open_tasks = true
        await updateProgrammeObligation(buildingPassportId, targetId, payload)
      } else {
        await updateTask({ id: targetId, formTemplateId: templateId })
      }
      toast.success(`This form is now used for ${name}.`)
      onDone(returnPathFor({ targetType, targetId, buildingPassportId }))
    } catch (error) {
      toast.error(serverMessage(error))
      setSaving(false)
    }
  }

  return createPortal(
    <Overlay onMouseDown={onClose}>
      <Card role='dialog' aria-modal='true' aria-labelledby={titleId} onMouseDown={e => e.stopPropagation()}>
        {loading ? (
          <>
            <SkeletonLine $w='80%' $h={22} />
            <SkeletonLine $w='100%' $h={14} />
          </>
        ) : (
          <>
            <Title id={titleId}>Use this form for {name}?</Title>
            <Message>
              {isObligation
                ? 'New tasks for this obligation will ask people to fill in this form.'
                : 'This task will ask people to fill in this form.'}
            </Message>
            {isObligation && target?.openTaskCount > 0 && (
              <CheckRow>
                <input
                  id={`${titleId}-apply`}
                  type='checkbox'
                  checked={applyToOpen}
                  onChange={e => setApplyToOpen(e.target.checked)}
                />
                <label htmlFor={`${titleId}-apply`}>{openTasksSentence(target.openTaskCount)}</label>
              </CheckRow>
            )}
          </>
        )}
        <Foot>
          <Ghost type='button' onClick={onClose} disabled={saving}>Not now</Ghost>
          <Primary type='button' onClick={applyForm} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Use this form'}
          </Primary>
        </Foot>
      </Card>
    </Overlay>,
    document.body
  )
}

export default AttachDraftDialog

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
`

const Card = styled.div`
  width: 440px;
  max-width: 100%;
  box-sizing: border-box;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(12, 18, 41, 0.16);
  padding: 22px 22px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  color: #0C1229;
  overflow-wrap: anywhere;
`

const Message = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #334155;
`

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 15px;
  line-height: 1.45;
  color: #0C1229;

  input { width: 20px; height: 20px; margin: 2px 0 0; flex: 0 0 auto; accent-color: #33becc; }
  label { cursor: pointer; }
`

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`

const btn = `
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 10px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`

const Ghost = styled.button`
  ${btn}
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0C1229;
  &:hover:not(:disabled) { background: #f1f5f9; }
`

const Primary = styled.button`
  ${btn}
  border: 1px solid #33becc;
  background: #33becc;
  color: #fff;
  &:hover:not(:disabled) { background: #2aa6b3; border-color: #2aa6b3; }
`
